import * as React from "react"
import {Bar, Line, Pie} from "react-chartjs-2"
import {ChartDataSets} from "chart.js"
import {
	FlatReportChartData, getDataById, getKeys, getDataByKey, getIds, InstantReportData, MultiReportData,
	ReportData, ReportChartRow, getActualData, FlatReportChartDataRow
} from "./chart-data"
import {mapValues, unique, dataFromSerial, reduceToObj} from "../utils"
import {FixedDataGrid, Row} from "../sbhtml/fixed-data-grid"
import {intersperse} from "ramda"
import {ReactNode} from "react";
import {ReportTypeKey} from "./lib2";

export type DisplayModes = {[index: string]: DataDisplayer}

export type Axes = {[index: string]: Axis}
export type Axis = AxisData[]
export type AxisData = {value: string, label: string}
const axisLabel = (id: string, map: Axis) => dataFromSerial(id, map, "Axis Data", 'value', 'label')

type InstantDataRendererProps = {
	chartData: InstantReportData
	borderColors: string[],
	fillColors: string[],
	axis: Axis
}
type RawMultiDataRendererProps = {
	chartData: MultiReportData
	borderColors: string[],
	fillColors: string[],
	axis: Axis
	removeEmptyAxes: boolean
	keys: ReportTypeKey[]
}
type ProcessedMultiDataRendererProps = {
	labels: string[],
	datasets: ChartDataSets[]
}
export type DataRendererProps = {
	chartData: ReportData,
	borderColors: string[],
	fillColors: string[],
	axis: Axis
	removeEmptyAxes: boolean
	keys: ReportTypeKey[]
}
type DataDisplayer = React.StatelessComponent<DataRendererProps>
export type DataModeDisplayer = DataDisplayer

export const RenderGraph = (props: DataRendererProps) => {
	const chartData = props.chartData
	switch (chartData.renderAs) {
		case "line":
		case "bar":
			return <RenderMultiGraph chartData={chartData} borderColors={props.borderColors} fillColors={props.fillColors} axis={props.axis} removeEmptyAxes={props.removeEmptyAxes} keys={props.keys} />
		case "pie":
			return <RenderPieGraph chartData={chartData} borderColors={props.borderColors} fillColors={props.fillColors}  axis={props.axis} />
	}
}

export const RenderMultiGraph = (props: RawMultiDataRendererProps) => {
	const d = props.chartData.data || []
	const datasets = extractDatasets(props.borderColors, props.fillColors, props.removeEmptyAxes ? getActualData(d) : d, props.axis)
	const labels = unique((props.chartData.data || []).map(d => d.key))

	const chartData = props.chartData
	switch (chartData.renderAs) {
		case "line":
			return <RenderLineGraph datasets={datasets} labels={labels} />
		case "bar":
			return <RenderBarGraph datasets={datasets} labels={labels} />
	}
}

const labelsFromInstantReportData = (data: InstantReportData, types: Axis): string[] => {
	//first we convert the data from an object into an array of tuples
	const asTuples = mapValues<number, [number, string], ReportChartRow>(data.data || {}, (v, k) => [v, k])
	// then we remove the types with zero answers
	const withoutBlanks = asTuples.filter(([v]) => v > 0)
	//then we get the labels
	return withoutBlanks.map(([v, k]) => axisLabel(k, types))
}

const RenderPieGraph = (props: InstantDataRendererProps) => <Pie data={{
	labels: labelsFromInstantReportData(props.chartData, props.axis),
	datasets: [{backgroundColor: props.fillColors, borderColor: props.borderColors, data: mapValues(props.chartData.data || {}, v => v).filter(v => v > 0)}]
}}/>

export const RenderBarGraph = (props: ProcessedMultiDataRendererProps) => {
	return <Bar
		options={{
			scales: {
				xAxes: [{stacked: true}],
				yAxes: [{stacked: true}]
			}, legend: {display: props.datasets.length <= 30}
		}}
		data={{
			labels: props.labels,
			datasets: props.datasets
		}} />
}

export const RenderLineGraph = (props: ProcessedMultiDataRendererProps) => {
	return <Line
		options={{ legend: {display: props.datasets.length <= 30} }}
		data={{
			labels: props.labels,
			datasets: props.datasets.map(d => ({
				...d,
				fill: false,
				lineTension: 0
			}))
		}} />
}

const extractDatasets = (borderColors: string[], fillColors: string[], d: FlatReportChartData, axis: Axis): ChartDataSets[] => getIds(d).map((id, n):ChartDataSets => ({
	label: axisLabel(id, axis),
	backgroundColor: fillColors[n],
	borderColor: borderColors[n],
	borderSkipped: "right",
	data: getDataById(d, id).map(d => d.value)
}))

type SelectModeProps<DD extends DisplayModes> = {
	modes: (keyof DD)[],
	mode: keyof DD,
	setMode: (newMode: keyof DD) => void
}

export const RenderSelectMode = <DD extends DisplayModes>(props: SelectModeProps<DD>) =>
	<span>
		{intersperse<ReactNode>(' | ', props.modes.map(mode => props.mode == mode
			? <b key={mode}>{mode}</b>
			: <a key={mode} style={{fontWeight: 'normal'}} href="javascript:void 0" onClick={() => (props.setMode(mode), false)}>{mode}</a>
		))}
	</span>

const RenderMultiTable = (props: RawMultiDataRendererProps) => {
	const chartData = props.chartData.data || [];
	const filteredData = getIds(props.removeEmptyAxes ? getActualData(chartData) : chartData)

	const headingFixed = reduceToObj<string, ReportTypeKey, string>((key) => "i_" + key.id, (key) => key.label, props.keys)
	const headingVar = reduceToObj<string, string, string>((id) => "i_" + id, (id) => axisLabel(id, props.axis), filteredData)
	const heading = {...headingFixed, ...headingVar}

	const makeKeyCells = (key: string): {[i: string]: {node: ReactNode}} => reduceToObj<string, ReportTypeKey, {node: ReactNode}>(
		(k) => "i_" + k.id,
		() => ({node: key}),
		props.keys
	)
	const makeDataCells = (key: string): {[i: string]: {node: ReactNode}} => reduceToObj<string, FlatReportChartDataRow, {node: ReactNode}>(
		(data) => "i_" + data.id,
		(data) => ({node: data.value}),
		getDataByKey(chartData, key)
	)
	const makeRow = (key: string): Row<string> => ({...makeKeyCells(key), ...makeDataCells(key)})

	const rows = getKeys(chartData)
	const data: Row<string>[] = rows.map(makeRow)

	return <FixedDataGrid heading={heading} data={data} />
}

const headingSingle = {"Type": "Membership Type", "Count": "Membership Count"}
class SingleTable extends FixedDataGrid<typeof headingSingle> {}

const RenderSingleTable = (props: InstantDataRendererProps) => {
	const data = mapValues(props.chartData.data || {}, (count, type) =>
		({"Type": {node: axisLabel(type, props.axis)}, "Count": {node: count + ""}})
	)
	return <SingleTable heading={headingSingle} data={data} />
}

const RenderTable =  (props: DataRendererProps): JSX.Element => {
	const chartData = props.chartData
	if (!chartData) {
		return <div>No data</div>
	}
	switch (chartData.renderAs) {
		case "line":
		case "bar":
			return <RenderMultiTable chartData={chartData} borderColors={props.borderColors} fillColors={props.fillColors} axis={props.axis} removeEmptyAxes={props.removeEmptyAxes} keys={props.keys} />
		case "pie":
			return <RenderSingleTable chartData={chartData} borderColors={props.borderColors} fillColors={props.fillColors} axis={props.axis} />
	}
}

export const standardModes: DisplayModes = {
	Graph: RenderGraph,
	Table: RenderTable
}

export type StandardDisplayModes = typeof standardModes



// WEBPACK FOOTER //
// ./src/lib/report-builder/graph-renderer.tsx